import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, Box, Typography, InputLabel, Alert } from '@mui/material';
import useUnifiedPackages from './useUnifiedPackages';
import AppSearchBar from './AppSearchBar';
import AppSelectionSummary from './AppSelectionSummary';
import AppCardGrid from './AppCardGrid';

function SelectAppStore(props) {
  const {
    installedPackages,
    baseImage,
    setRepoPkgSel,
    setRepoPkgBundleSel,
    setIsPkgListModified,
    appvolOnly = false
  } = props;

  const { packages: rawPackages, bundlePackages: rawBundlePackages, categories: rawCategories, loading, error } = useUnifiedPackages(baseImage, { appvolOnly });

  // Se appvolOnly, filtra per mostrare solo le app appvol
  const packages = useMemo(() => appvolOnly ? rawPackages.filter(p => p.source === 'appvol') : rawPackages, [rawPackages, appvolOnly]);
  const bundlePackages = useMemo(() => appvolOnly ? [] : rawBundlePackages, [rawBundlePackages, appvolOnly]);
  const categories = useMemo(() => {
    if (!appvolOnly) return rawCategories;
    const catSet = new Set();
    packages.forEach(p => { if (p.category) catSet.add(p.category); });
    return Array.from(catSet);
  }, [rawCategories, packages, appvolOnly]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategories, setActiveCategories] = useState([]);

  // IDs bloccati (bundle)
  const lockedIds = useMemo(
    () => new Set(bundlePackages.map(p => p.id)),
    [bundlePackages]
  );

  // Sync installedPackages -> selectedIds solo al primo caricamento
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (packages.length > 0 && installedPackages && installedPackages.length > 0) {
      const initialSelected = new Set();
      installedPackages.forEach(pkg => {
        const found = packages.find(p =>
          p.name === pkg.value &&
          (pkg.source === p.source || (!pkg.source && p.source === 'devops')) &&
          (p.source !== 'appvol' ||
            !pkg.sourceDetail?.applicationId ||
            p.sourceDetail?.applicationId === pkg.sourceDetail?.applicationId)
        );
        if (found) initialSelected.add(found.id);
      });
      setSelectedIds(prev => {
        const merged = new Set(prev);
        initialSelected.forEach(id => merged.add(id));
        return merged;
      });
      initializedRef.current = true;
    }
  }, [packages, installedPackages]);

  // Toggle selezione
  const handleToggle = useCallback((id) => {
    if (lockedIds.has(id)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    if (setIsPkgListModified) setIsPkgListModified(true);
  }, [lockedIds, setIsPkgListModified]);

  // Rimozione dal summary
  const handleRemove = useCallback((id) => {
    handleToggle(id);
  }, [handleToggle]);

  // Sync verso parent callbacks (formato {label, value, source, sourceDetail})
  useEffect(() => {
    const selectedPkgs = packages
      .filter(p => selectedIds.has(p.id))
      .map(p => ({
        label: p.name,
        value: p.name,
        source: p.source,
        sourceDetail: p.source === 'appvol' ? p.sourceDetail : undefined,
        version: p.version || '',
        description: p.description || '',
      }));
    const bundlePkgs = bundlePackages
      .map(p => ({
        label: p.name,
        value: p.name,
        source: p.source,
        sourceDetail: p.source === 'appvol' ? p.sourceDetail : undefined,
        version: p.version || '',
        description: p.description || '',
      }));

    setRepoPkgSel(selectedPkgs);
    setRepoPkgBundleSel(bundlePkgs);
  }, [selectedIds, packages, bundlePackages, setRepoPkgSel, setRepoPkgBundleSel]);

  // Toggle categoria
  const handleCategoryToggle = useCallback((category) => {
    if (category === null) {
      setActiveCategories([]);
      return;
    }
    setActiveCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      }
      return [...prev, category];
    });
  }, []);

  // Ricerca debounced handler
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  // Filtraggio client-side
  const filteredApps = useMemo(() => {
    let result = [...bundlePackages, ...packages];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q))
      );
    }

    if (activeCategories.length > 0) {
      result = result.filter(p => activeCategories.includes(p.category));
    }

    return result;
  }, [packages, bundlePackages, searchQuery, activeCategories]);

  // App selezionate per il summary
  const selectedApps = useMemo(
    () => packages.filter(p => selectedIds.has(p.id)),
    [packages, selectedIds]
  );

  return (
    <Card sx={{ padding: 3 }}>
      <Box>
        <InputLabel sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0.5 }}>
          Selezione applicazioni
        </InputLabel>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona le applicazioni da includere nel tuo laboratorio.
          Cerca, filtra per categoria e clicca sulle card per aggiungerle.
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Alcune sorgenti dati non sono disponibili. I pacchetti mostrati potrebbero essere parziali.
          </Alert>
        )}

        {/* Riepilogo selezione */}
        <AppSelectionSummary
          selectedApps={selectedApps}
          bundleApps={bundlePackages}
          onRemove={handleRemove}
        />

        {/* Barra ricerca e filtri */}
        <AppSearchBar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          categories={categories}
          activeCategories={activeCategories}
          onCategoryToggle={handleCategoryToggle}
        />

        {/* Griglia card */}
        <AppCardGrid
          apps={filteredApps}
          selectedIds={selectedIds}
          lockedIds={lockedIds}
          onToggle={handleToggle}
          loading={loading}
        />
      </Box>
    </Card>
  );
}

export default SelectAppStore;
